import { FormattedMessage } from 'react-intl'
import { Box, List, ListItem, styled, useTheme } from '@mui/material'
import { Typography } from '@carto/meridian-ds/components'
import { useEffect, useRef, useState } from 'react'
import { usePostHelpQuestionHook } from '../hooks/helpPanelHooks'
import SearchTextField from 'features/common/ui/Search/SearchTextField'
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined'
import SubdirectoryArrowLeftIcon from '@mui/icons-material/SubdirectoryArrowLeft'
import RoundedIcon from 'features/common/ui/RoundedIcon'
import IconSearch from 'assets/icons/icon-action-search.svg?react'
import QuestionAnswer from './QuestionAnswer'
import intl from 'app/localization/intl'
import { createMetricRequest } from 'features/metrics/state/metricsSlice'
import { MetricEventType } from 'features/metrics/api/metrics'
import { useAppDispatch } from 'app/state'
import { ErrorBoundary } from 'features/common/ui/ErrorBoundary'

const PanelContent = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  flex: '1 1 auto',
  flexDirection: 'column',
  gap: theme.spacing(3),
  overflowY: 'auto'
}))

const ResponsePanel = styled(Box)(() => ({
  display: 'inline-flex',
  flex: '1 1 auto',
  flexDirection: 'column',
  overflowY: 'auto'
}))

const IconEnterStyled = styled(SubdirectoryArrowLeftIcon)(({ theme }) => ({
  fill: theme.palette.text.hint
}))

const IconAwesomeStyled = styled(AutoAwesomeOutlinedIcon)(({ theme }) => ({
  path: {
    fill: theme.palette.primary.light
  }
}))

const IconSearchStyled = styled(IconSearch)(() => ({
  path: {
    fillOpacity: 1
  }
}))

const AskingTypography = styled(Typography)(({ theme }) => ({ color: `${theme.palette.text.secondary} !important` }))

const ListItemStyled = styled(ListItem)(({ theme }) => ({
  padding: theme.spacing(0.75, 1),
  margin: theme.spacing(0.5, 0),
  cursor: 'pointer',
  gap: theme.spacing(1),
  '&:hover': {
    backgroundColor: theme.palette.action.hover
  }
}))

const QuestionPanel = () => {
  const dispatch = useAppDispatch()

  const [query, setQuery] = useState('')
  const [queryRequest, setQueryRequest] = useState('')
  const [showEndAdornment, setShowEndAdornment] = useState(true)
  const { isLoading, data: questionResult, isError } = usePostHelpQuestionHook(queryRequest)
  const theme = useTheme()
  const inputRef = useRef<HTMLInputElement>(null)

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(event.target.value)
  }

  const handleKeyUp = (event) => {
    if (event.key === 'Enter') {
      // KeyboardEvent only has 'currentTarget' property defined, but if we use that one we get an error,
      // so we need to force a casting.
      const eventTarget = event.target as HTMLInputElement
      setQueryRequest(eventTarget.value)
      setShowEndAdornment(false)
      dispatch(createMetricRequest(MetricEventType.HelpSidebarSearchQuery))
    }
  }

  const onResetSearch = () => {
    setQueryRequest('')
    setQuery('')
    setShowEndAdornment(true)
  }

  const handleClickQuestion = (question: string) => {
    setQueryRequest(question)
    setQuery(question)
    setShowEndAdornment(false)
    dispatch(createMetricRequest(MetricEventType.HelpSidebarSearchQuery))
  }

  const questions = Array.from({ length: 5 }, (_, index) => ({
    id: index + 1,
    message: intl.formatMessage({ id: `helpPanel.suggestedQuestions.${index + 1}` })
  }))

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  return (
    <PanelContent>
      <ErrorBoundary>
        <SearchTextField
          inputRef={inputRef}
          onResetSearch={onResetSearch}
          value={query}
          onChange={handleChange}
          onKeyUp={handleKeyUp}
          placeholder={intl.formatMessage({ id: 'helpPanel.questionPanel.askAnything' })}
          size="medium"
          startAdornment={<IconAwesomeStyled />}
          endAdornment={showEndAdornment ? <IconEnterStyled onKeyUp={handleKeyUp} /> : undefined}
        />
        <ResponsePanel>
          {queryRequest !== '' && (
            <QuestionAnswer question={queryRequest} answer={questionResult} isLoading={isLoading} isError={isError} />
          )}
          {query === '' && (
            <>
              <AskingTypography variant="body2">
                <FormattedMessage id="helpPanel.questionPanel.subtitle" />
              </AskingTypography>
              <List>
                {questions.map((question) => (
                  <ListItemStyled key={`question-${question.id}`} onClick={() => handleClickQuestion(question.message)}>
                    <RoundedIcon
                      icon={<IconSearchStyled />}
                      size="small"
                      iconColor={theme.palette.primary.main}
                      backgroundColor={theme.palette.primary.background}
                    />
                    <Typography variant="body2">{question.message}</Typography>
                  </ListItemStyled>
                ))}
              </List>
            </>
          )}
        </ResponsePanel>
      </ErrorBoundary>
    </PanelContent>
  )
}

export default QuestionPanel
